import React from 'react'
import { useNavigate, useLoaderData } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { usePaymentResultRealtime } from '../../hooks/usePaymentResultRealtime'
import { formatNumber } from '../../utils/formatters'
import { PaymentResult } from '../../types/PaymentResult'
import ArrowButton from '../../components/ArrowButton/ArrowButton'
import Details from '../../components/Details/Details'
import Spinner from '../../components/Spinner/Spinner'
import ErrorStatusIcon from '../../components/StatusIcon/ErrorStatusIcon'
import FailedStatusIcon from '../../components/StatusIcon/FailedStatusIcon'
import SuccessStatusIcon from '../../components/StatusIcon/SuccessStatusIcon'
import MerchantSiteRedirectButton from '../../components/MerchantSiteRedirectButton/MerchantSiteRedirectButton'
import styles from './PaymentResultPage.module.css'

const PaymentResultPage = () => {
    const navigate = useNavigate()

    const initialResult = useLoaderData<PaymentResult>()

    const needsRealtime = initialResult.status === 'processing'

    const { paymentResult: realtimeResult } = usePaymentResultRealtime(
        initialResult.paymentPageId,
        initialResult.transactionId,
        needsRealtime,
    )

    // Используем realtime результат если есть, иначе initial
    const paymentResult: PaymentResult = realtimeResult || initialResult
    const loading = needsRealtime && !realtimeResult

    const renderIcon = () => {
        if (loading) return <Spinner />
        if (paymentResult?.status === 'processing') return <Spinner />
        if (paymentResult?.status === 'success') return <SuccessStatusIcon />
        if (paymentResult?.status === 'failed') return <FailedStatusIcon />
        return <ErrorStatusIcon />
    }

    const renderTitle = () => {
        if (loading) return t`Обработка платежа...`
        if (paymentResult?.status === 'processing') return t`Обработка платежа...`
        if (paymentResult?.status === 'success') return t`Платеж успешно выполнен`
        if (paymentResult?.status === 'failed') return t`Платеж отклонен`
        return t`Ошибка`
    }

    const renderDescription = () => {
        if (loading) return t`Ваш платеж обрабатывается. Пожалуйста, подождите.`
        if (paymentResult?.status === 'processing') return t`Ваш платеж обрабатывается. Пожалуйста, подождите.`
        if (paymentResult?.status === 'success') return t`Спасибо за оплату! Ваша транзакция успешно завершена.`
        if (paymentResult?.status === 'failed') return t`К сожалению, платеж не может быть выполнен. Попробуйте другой способ оплаты.`
        return t`Не удалось получить результат платежа. Пожалуйста, попробуйте позже.`
    }

    const details = paymentResult ? [
        { label: t`ID транзакции:`, value: paymentResult.transactionId.toString() },
        { label: t`Сумма:`, value: `${formatNumber(paymentResult.amount / 100)} ${paymentResult.currency}` },
    ] : []

    return (
        <div className={styles.section}>
            {renderIcon()}

            <h1 className={styles.title}>{renderTitle()}</h1>
            <p className={styles.description}>{renderDescription()}</p>

            {paymentResult?.status === 'failed' && (
                <div className={styles.alternative}>
                    <ArrowButton
                        icon="💳"
                        name={t`Попробовать другой способ`}
                        onClick={() => navigate(`/payment/${paymentResult.paymentPageId}`)}
                        disabled={false}
                    />
                </div>
            )}

            {!!details.length && <Details items={details} accentValues={true} />}

            {paymentResult && paymentResult.status !== 'processing' && (
                <MerchantSiteRedirectButton
                    redirectUrl={paymentResult.redirectUrl}
                    redirectTimeout={paymentResult.redirectTimeout}
                />
            )}
        </div>
    )
}

export default PaymentResultPage
